import {
  Box,
  Button,
  Card,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Tab,
  Tabs,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import ReactInputMask from "react-input-mask";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { AuthContext } from "../../contexts/auth";
import ClientService from "../../services/client.service";
import FileUploadService from "../../services/fileUpload.service";
import { SEGMENT_OPTIONS } from "../../utils/customerSegments";
import SpreadsheetTab from "../novos/Customers/SpreadsheetTab";
import ManagementFieldsTab from "../novos/Customers/ManagementFieldsTab";
import CustomOptionsTab from "../novos/Customers/CustomOptionsTab";
import RightSideBar from "../novos/Customers/RightSideBar";
import { LabeledInput } from "../UI/LabeledInput";
import SelectForm from "../UI/Select";
import YesOrNoSwitch from "../UI/YesOrNoSwitch";
import { maskCPFOrCNPJ } from "../../utils/formatters";

type Props = {
  edit: any;
};

// Moldura das abas de configuração do cliente (Campos Gerenciais e Opções
// Personalizadas).
const tabPanelStyle = {
  padding: "20px 20px",
  boxShadow: "0px 0px 2px 1px #C4C4C4",
  borderRadius: 4,
  display: "flex",
  flexDirection: "column",
  width: "100%",
} as const;

export function Customer({ edit }: Props) {
  const { actionOptions } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  // URLSearchParams em vez de split("="): com mais de um parametro na URL
  // (?customerId=X&tab=1) o split devolvia "X&tab" como id.
  const customerId = new URLSearchParams(location.search).get("customerId");
  const [activeTab, setActiveTab] = useState(0);

  // A key precisa incluir customerId: sem isso, o react-query nunca refaz a
  // busca ao trocar de cliente sem desmontar o componente (mesma rota,
  // customerId novo na URL) -- ficava mostrando pra sempre os dados do
  // PRIMEIRO cliente carregado na sessao, mesmo clicando em outro na lista
  // ("todos estao chamando o cliente fixo").
  const { data: clientData, isLoading: clientLoading } = useQuery({
    queryKey: ["client", customerId],
    queryFn: () => ClientService.getClients({ id: customerId }),
    enabled: !!customerId,
  });

  const [state, setState] = useState({} as any);
  const [taxId, setTaxId] = useState("");
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // URL local (blob:) do arquivo recem-selecionado, pra poder revogar
  // (URL.revokeObjectURL) assim que trocarmos pra URL definitiva e evitar
  // vazamento de memoria.
  const localLogoObjectUrlRef = useRef<string | null>(null);
  // Marca que ha um logo recem-anexado ainda nao salvo (submit). O useQuery
  // de clientData refetcha em segundo plano (foco de janela, etc.) mesmo sem
  // o usuario ter salvo nada -- sem essa guarda, o efeito de carga abaixo
  // reagia a esse refetch e sobrescrevia a preview nova pelo logo_url ANTIGO
  // do banco, causando o "mostra e depois some/quebra" (#MMV-2532).
  const pendingLogoUploadRef = useRef(false);

  const revokeLocalLogoObjectUrl = () => {
    if (localLogoObjectUrlRef.current) {
      URL.revokeObjectURL(localLogoObjectUrlRef.current);
      localLogoObjectUrlRef.current = null;
    }
  };

  // Revoga a ultima URL local ao desmontar (troca de cliente ja revoga na
  // hora, ver handleLogoChange/useEffect de limpeza abaixo).
  useEffect(() => revokeLocalLogoObjectUrl, []);

  // Limpa o formulario quando o cliente da URL muda sem o componente
  // desmontar (voltar/avancar do navegador, link direto para outro cliente).
  // Precisa vir ANTES do efeito que preenche o state para nao apagar o dado
  // recem-carregado quando os dois efeitos rodam no mesmo commit.
  useEffect(() => {
    revokeLocalLogoObjectUrl();
    pendingLogoUploadRef.current = false;
    setState({} as any);
    setTaxId("");
    setLogoPreview(null);
    setLogoLoadFailed(false);
    setActiveTab(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => {
    // Ha um upload de logo recem-feito e ainda nao salvo -- não deixa um
    // refetch em segundo plano (foco de janela, etc.) sobrescrever a preview
    // nova pelo que ainda está no banco.
    if (pendingLogoUploadRef.current) {
      return;
    }
    if (!!clientData) {
      setState(clientData);

      const formData: any = clientData;

      if (formData?.tax_id) {
        const formattedValue = maskCPFOrCNPJ(formData?.tax_id);
        setTaxId(formattedValue);
      }

      if (formData?.logo_url) {
        console.log(
          "[LogoDebug][Customer] useEffect logo_url from DB:",
          formData.logo_url
        );
        setLogoLoadFailed(false);
        setLogoPreview(formData.logo_url);
        ClientService.getLogoPresignedUrl(formData.logo_url)
          .then((presignedUrl) => {
            console.log(
              "[LogoDebug][Customer] useEffect presignedUrl:",
              presignedUrl
            );
            if (presignedUrl) {
              setLogoLoadFailed(false);
              setLogoPreview(presignedUrl);
            }
          })
          .catch((err) => {
            console.error(
              "[LogoDebug][Customer] useEffect getLogoPresignedUrl ERROR:",
              err
            );
          });
      }
    }
  }, [clientData]);

  //   {
  //   _id: edit?._id || undefined,
  //   handle_id: edit?.handle_id || null, // como saber qual handle id passar?
  //   name: edit?.name || "",
  //   nickname: edit?.nickname || "",
  //   segment: edit?.segment || "",
  //   email: edit?.email || "",
  //   tax_id: edit?.tax_id || "",
  //   country: edit?.country || "",
  //   state: edit?.state || "",
  //   city: edit?.city || "",
  //   neighborhood: edit?.neighborhood || "",
  //   address: edit?.address || "",
  //   number: edit?.number || "",
  //   zip_code: edit?.zip_code || "",
  //   phone: edit?.phone || "",
  //   street_type: edit?.street_type || "",
  //   complement: edit?.complement || "",
  //   latitude: edit?.latitude || 0,
  //   longitude: edit?.longitude || 0,
  //   origin: edit?.origin || "",
  //   business_group: edit?.business_group || "",
  //   business_group_name: edit?.business_group_name || "",
  //   custom_fields: edit?.custom_fields || "",
  //   companies: edit?.companies || {},
  //   contracts: edit?.contracts || "",
  //   agency_contract: edit?.agency_contract || true,
  //   events_contract: edit?.events_contract || true,
  //   has_supplier: edit?.has_supplier || false,
  //   collaborator_mm: edit?.collaborator_mm || true,
  //   physical_person: edit?.physical_person || false,
  //   juridical_person: edit?.juridical_person || false,
  //   national: edit?.national || false,
  //   international: edit?.international || false,
  //   cnpj_cpf: edit?.cnpj_cpf || "",
  //   client_requester: edit?.client_requester || "",
  //   external_client: edit?.external_client || true,
  // });
  const { mutate: fetchCnpjData, isLoading: isLoadingFetchCnpj } = useMutation(
    ClientService.fetchCnpjData,
    {
      onSuccess: (apiData) => {
        if (apiData) {
          setState((prevState: any) => ({
            ...prevState,
            zip_code: apiData.CEP || "",
            country: "Brasil",
            state: apiData.UF || "",
            city: apiData.MUNICIPIO || "",
            neighborhood: apiData.BAIRRO || "",
            street_type: apiData.LOGRADOURO || "",
            email: apiData.EMAIL || "",
            number: apiData.NUMERO || "",
            complement: apiData.COMPLEMENTO || "",
          }));
        }
      },
      onError: (error) => {
        console.error("Error fetching CNPJ data", error);
      },
    }
  );

  const { mutate: createClient, isLoading: isLoadingCreate } = useMutation(
    ClientService.create,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["client"]);
        toast.success("Client criado com sucesso!", {
          toastId: "client-create-success",
        });
        // Sem isso, a lista (queryKey "clients_search") ficava servindo o
        // resultado em cache (refetchOnMount: false no QueryClient global)
        // e o cliente recem-criado so aparecia depois de 5min/hard refresh.
        queryClient.invalidateQueries(["clients_search"]);
        history.push("/partners/customers");
      },
      onError: (error) => {
        toast.error("Não foi possível criar o cliente");
      },
    }
  );

  const { mutate: updateClient, isLoading: isLoadingUpdate } = useMutation(
    (data: any) => ClientService.update(state._id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["client"]);
        toast.success("Cliente atualizado com sucesso!", {
          toastId: "client-update-success",
        });
        // Mesmo motivo: sem invalidar, reabrir esse cliente (ou a lista)
        // mostrava os dados de ANTES de salvar -- inclusive a logo antiga
        // -- porque o QueryClient global tem refetchOnMount:false/staleTime
        // de 5min, entao o cache velho ficava sendo servido ate expirar ou
        // um hard refresh forcar tudo de novo (#MMV-2532).
        queryClient.invalidateQueries(["client", state._id]);
        queryClient.invalidateQueries(["clients_search"]);
        history.push("/partners/customers");
      },
      onError: (error) => {
        toast.error("Não foi possível atualizar o cliente");
      },
    }
  );
  const handleSubmmit = (e: React.MouseEvent) => {
    if (isLoadingCreate || isLoadingUpdate) return;
    if (uploadingLogo) {
      toast.warn("Aguarde o upload do logotipo terminar antes de salvar.");
      return;
    }

    const data = {
      ...state,
    };
    delete data._id;
    delete data.updated_at;
    console.log(
      "[LogoDebug][Customer] handleSubmmit logo_url:",
      state.logo_url,
      "isEdit:",
      !!state._id
    );
    state._id ? updateClient(data) : createClient({ ...data, origin: "MMIDD" });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.name === "number") {
      const numberRegex = new RegExp("^[0-9]*$");
      if (!numberRegex.test(e.target.value)) {
        e.target.value = e.target.value.replace(/\D/g, "");
      }
    }

    if (e.target.name === "tax_id") {
      const isCPF = e.target.value.length <= 14;

      const taxState = {
        physical_person: isCPF,
        juridical_person: !isCPF,
      };

      const formattedValue = maskCPFOrCNPJ(e.target.value);
      setTaxId(formattedValue);

      setState({
        ...state,
        ...taxState,
        [e.target.name]: e.target.value,
      });

      return;
    }
    setState({
      ...state,
      [e.target.name]: e.target.value,
    });
  };

  const handleTaxIdBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const unmaskedCnpj = value.replace(/\D/g, "");
    fetchCnpjData(unmaskedCnpj);
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpg", "image/jpeg"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Formato inválido. Utilize PNG, JPG ou JPEG.");
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("O arquivo deve ter no máximo 2 MB.");
      return;
    }

    // Preview local (blob:) IMEDIATA, antes de qualquer chamada de rede --
    // não depende do upload nem do presign do S3 pra mostrar algo no
    // quadro, então nunca fica sem renderizar enquanto sobe/mesmo se o
    // presign falhar depois. Revoga a blob anterior (se houver) pra não
    // vazar memória.
    revokeLocalLogoObjectUrl();
    const localObjectUrl = URL.createObjectURL(file);
    localLogoObjectUrlRef.current = localObjectUrl;
    pendingLogoUploadRef.current = true;
    setLogoLoadFailed(false);
    setLogoPreview(localObjectUrl);

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append(
        "data",
        JSON.stringify({
          area: "project",
          project: state._id || "000000000000000000000000",
          fileName: file.name,
        })
      );

      const uploadResponse = await FileUploadService.create(formData);
      const rawFileLink = uploadResponse?.fileLink;

      console.log(
        "[LogoDebug][Customer] Upload response fileLink:",
        rawFileLink
      );

      if (!rawFileLink) {
        toast.error("Erro ao obter URL do logotipo. Mostrando a prévia local.");
        return;
      }

      let previewUrl = rawFileLink;
      if (uploadResponse?._id) {
        try {
          const fileData = await FileUploadService.getById(uploadResponse._id);
          if (fileData?.fileLink) {
            previewUrl = fileData.fileLink;
          }
        } catch {}
      }

      setState((prev: any) => ({ ...prev, logo_url: rawFileLink }));
      toast.success("Logotipo anexado com sucesso!");

      // O fileLink que volta do upload é o mesmo tipo de referência "crua"
      // (chave S3) que já vem de formData.logo_url no useEffect de carga —
      // lá ele passa por getLogoPresignedUrl antes de virar preview; aqui
      // faltava esse passo, então a <img> tentava abrir a chave crua direto
      // e quebrava. Mesmo tratamento do useEffect: pega a URL assinada e só
      // então troca — a preview local (blob) continua no quadro até lá, pra
      // nunca ficar sem renderizar nesse meio-tempo.
      try {
        const presignedUrl = await ClientService.getLogoPresignedUrl(
          previewUrl
        );
        console.log(
          "[LogoDebug][Customer] handleLogoChange presignedUrl:",
          presignedUrl
        );
        if (presignedUrl) {
          revokeLocalLogoObjectUrl();
          setLogoLoadFailed(false);
          setLogoPreview(presignedUrl);
        }
      } catch (err) {
        console.error(
          "[LogoDebug][Customer] handleLogoChange getLogoPresignedUrl ERROR:",
          err
        );
        // Falhou o presign -- mantém a prévia local (blob) no quadro em vez
        // de trocar por uma URL que sabidamente não vai renderizar.
      }
    } catch (err) {
      console.error("[LogoUpload] Erro no upload:", err);
      toast.error(
        "Erro ao fazer upload do logotipo. Mostrando a prévia local até tentar de novo."
      );
      // Mantém a prévia local (blob) já setada no início -- o usuário vê o
      // que escolheu mesmo quando o upload falha, em vez de ficar sem nada.
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };
  const isDisabled =
    !actionOptions.updateParceiros || !actionOptions.createParceiros;

  const getTitle = () => {
    if (state._id) return `${state?.handle_id} - ${state?.name}`;
    else return "Criar cliente";
  };

  return (
    <Box
      display={"flex"}
      width={"100%"}
      columnGap={2}
      py={1}
      flexDirection={"column"}
    >
      <span style={{ fontSize: 20, marginBottom: 8 }}>{getTitle()}</span>

      {edit && (
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 1, borderBottom: "1px solid #eee" }}
        >
          <Tab label="Informacoes" sx={{ textTransform: "none" }} />
          <Tab label="Planilha do cliente" sx={{ textTransform: "none" }} />
          <Tab label="Campos Gerenciais" sx={{ textTransform: "none" }} />
          <Tab label="Opções Personalizadas" sx={{ textTransform: "none" }} />
        </Tabs>
      )}

      {activeTab === 1 && edit && <SpreadsheetTab clientId={edit._id} />}

      {activeTab === 2 && edit && (
        <Box style={tabPanelStyle}>
          <ManagementFieldsTab
            fields={state.management_fields || []}
            onChange={(fields) =>
              setState({ ...state, management_fields: fields })
            }
            disabled={isDisabled}
          />
        </Box>
      )}

      {activeTab === 3 && edit && (
        <Box style={tabPanelStyle}>
          <CustomOptionsTab
            customFieldOptions={state.custom_field_options || []}
            onChange={(customFieldOptions) =>
              setState({ ...state, custom_field_options: customFieldOptions })
            }
            disabled={isDisabled}
          />
        </Box>
      )}

      {activeTab === 0 && (
        <Box display={"flex"} width={"100%"} columnGap={2} py={1}>
          <Box
            style={{
              padding: "20px 20px",
              boxShadow: "0px 0px 2px 1px #C4C4C4",
              borderRadius: 4,
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
            }}
          >
            <Box marginBottom={2}>
              <span style={{ fontWeight: "bold", fontSize: 16 }}>
                Informações básicas
              </span>
            </Box>

            <Box display={"flex"} flexDirection={"column"} width={"100%"}>
              <Box display={"flex"} gap={4}>
                <FormControl>
                  <FormLabel>Fornecedor</FormLabel>
                  <YesOrNoSwitch
                    setValue={(_, value) => {
                      if (value !== null)
                        setState({
                          ...state,
                          has_supplier: value,
                        });
                    }}
                    value={!!state.has_supplier}
                    type=""
                    align="flex-start"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Colaborador MM</FormLabel>

                  <YesOrNoSwitch
                    setValue={(_, value) => {
                      if (value !== null)
                        setState({
                          ...state,
                          collaborator_mm: value,
                        });
                    }}
                    value={!!state.collaborator_mm}
                    type=""
                    align="flex-start"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Internacional</FormLabel>

                  <YesOrNoSwitch
                    setValue={(_, value) => {
                      if (value !== null)
                        setState({
                          ...state,
                          international: value,
                          national: !value,
                        });
                    }}
                    value={!!state.international}
                    type=""
                    align="flex-start"
                  />
                </FormControl>
              </Box>

              <FormControl style={{ width: "30%", marginTop: 16 }}>
                <SelectForm
                  label="Segmento do Cliente"
                  value={state.segment}
                  onChange={(e) =>
                    setState({ ...state, segment: e.target.value })
                  }
                >
                  {SEGMENT_OPTIONS.map((value, index) => (
                    <MenuItem key={index} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </SelectForm>
              </FormControl>

              <LabeledInput
                size="small"
                label={"CPF/CNPJ"}
                name="tax_id"
                value={taxId}
                onChange={handleChange}
                onBlur={handleTaxIdBlur}
                placeholder={
                  state.physical_person
                    ? "000.000.000-00"
                    : "00.000.000/0000-00"
                }
              />
              {/* <LabeledInput
              size="small"
              label="Solicitante do Cliente"
              name="client_requester"
              value={state.client_requester}
              onChange={handleChange}
            /> */}
              <LabeledInput
                size="small"
                label="Nome"
                name="name"
                value={state.name}
                onChange={handleChange}
              />
              <LabeledInput
                size="small"
                label="Nome de uso/apelido"
                name="name"
                value={state.name}
                onChange={handleChange}
              />
              <Box display={"flex"} gap={2} alignItems={"flex-start"}>
                <Box flex={1}>
                  <LabeledInput
                    size="small"
                    label="Nome Grupo"
                    name="business_group_name"
                    value={state.business_group_name}
                    onChange={handleChange}
                  />
                </Box>
                <Box flex={1} display={"flex"} flexDirection={"column"}>
                  <InputLabel style={{ fontSize: "13px" }}>
                    Logotipo para Export
                  </InputLabel>
                  <Box display={"flex"} alignItems={"center"} gap={1} mt={1}>
                    {logoPreview ? (
                      <>
                        {logoLoadFailed ? (
                          // A URL setada (assinada ou local) falhou ao carregar de fato --
                          // mostra um placeholder legível em vez do ícone padrão de
                          // imagem quebrada do navegador (#MMV-2532).
                          <Box
                            sx={{
                              height: 120,
                              width: 120,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                              fontSize: 12,
                              color: "text.secondary",
                              border: "1px dashed #E0E0E0",
                              borderRadius: 1,
                              padding: "4px",
                            }}
                          >
                            Não foi possível carregar a prévia
                          </Box>
                        ) : (
                          <Box
                            component="img"
                            src={logoPreview}
                            alt="Logotipo"
                            onError={() => setLogoLoadFailed(true)}
                            sx={{
                              height: 120,
                              maxWidth: 250,
                              objectFit: "contain",
                              border: "1px solid #E0E0E0",
                              borderRadius: 1,
                              padding: "4px",
                            }}
                          />
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          style={{
                            textTransform: "none",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleLogoClick}
                          disabled={uploadingLogo}
                        >
                          {uploadingLogo ? "Enviando..." : "Alterar"}
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        style={{ textTransform: "none" }}
                        onClick={handleLogoClick}
                        disabled={uploadingLogo}
                      >
                        {uploadingLogo ? "Enviando..." : "Anexar"}
                      </Button>
                    )}
                  </Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpg,image/jpeg"
                    style={{ display: "none" }}
                    onChange={handleLogoChange}
                  />
                </Box>
              </Box>

              <Box
                style={{ height: 1, width: "100%", backgroundColor: "#E0E0E0" }}
                marginBottom={3}
                marginTop={3}
              />

              <Box marginBottom={2}>
                <span style={{ fontWeight: "bold", fontSize: 16 }}>
                  Contato
                </span>
              </Box>

              <LabeledInput
                size="small"
                fullWidth
                label="E-mail"
                name="email"
                value={state.email}
                onChange={handleChange}
              />
              <ReactInputMask
                // @ts-ignore
                maskPlaceholder={null}
                mask="(99) 99999-9999"
                value={state.phone}
                onChange={handleChange}
                name="phone"
              >
                <LabeledInput
                  size="small"
                  label="Telefone"
                  name="phone"
                  value={state.phone}
                  onChange={handleChange}
                  placeholder="(00) 0 0000 0000"
                />
              </ReactInputMask>

              <Box
                style={{ height: 1, width: "100%", backgroundColor: "#E0E0E0" }}
                marginBottom={3}
                marginTop={3}
              />

              <Box marginBottom={2}>
                <span style={{ fontWeight: "bold", fontSize: 16 }}>
                  Endereço
                </span>
              </Box>

              <ReactInputMask
                // @ts-ignore
                maskPlaceholder={null}
                mask="99999-999"
                value={state.zip_code}
                onChange={handleChange}
                name="zip_code"
              >
                <LabeledInput
                  size="small"
                  label="CEP"
                  name="zip_code"
                  value={state.zip_code}
                  onChange={handleChange}
                  placeholder="00000-000"
                />
              </ReactInputMask>
              <LabeledInput
                size="small"
                fullWidth
                label="País"
                value={state.country}
                name="country"
                onChange={handleChange}
              />

              <LabeledInput
                size="small"
                fullWidth
                label="Estado"
                value={state.state}
                name="state"
                onChange={handleChange}
              />

              <LabeledInput
                size="small"
                fullWidth
                label="Município"
                value={state.city}
                name="city"
                onChange={handleChange}
              />

              <LabeledInput
                size="small"
                fullWidth
                label="Bairro"
                value={state.neighborhood}
                name="neighborhood"
                onChange={handleChange}
              />

              <LabeledInput
                size="small"
                fullWidth
                label="Logradouro"
                value={state.street_type}
                name="street_type"
                onChange={handleChange}
              />
              <Box display={"flex"} width={"100%"} columnGap={1}>
                <LabeledInput
                  size="small"
                  style={{ width: "100%" }}
                  fullWidth
                  label="Número"
                  value={state.number}
                  name="number"
                  onChange={handleChange}
                />

                <LabeledInput
                  size="small"
                  style={{ width: "100%" }}
                  fullWidth
                  label="Complemento"
                  value={state.complement}
                  name="complement"
                  onChange={handleChange}
                />
              </Box>
            </Box>
          </Box>
          {edit && <RightSideBar editClient={edit} showTabs={false} />}
        </Box>
      )}
      <Box
        display={"flex"}
        justifyContent={"flex-end"}
        width={"100%"}
        marginTop={edit ? 3 : 4}
        paddingRight={edit ? "20px" : 0}
      >
        <Button
          style={{
            marginRight: "15px",
            textTransform: "none",
            fontSize: 16,
          }}
          variant="outlined"
          onClick={() => history.push("/partners/customers")}
        >
          Cancelar
        </Button>

        <Button
          style={{
            background: "#1361A4",
            color: "#FFFFFF",
            textTransform: "none",
            fontSize: 16,
          }}
          disabled={
            isDisabled || isLoadingCreate || isLoadingUpdate || uploadingLogo
          }
          variant="contained"
          onClick={handleSubmmit}
        >
          Salvar e Fechar
        </Button>
      </Box>
    </Box>
  );
}
